import _ from "lodash";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";

// Utils
import { getSelectedFieldHasValue } from "../../modules/Utils";
import {
  plannerElementTypesLabelMapping,
  getFieldValue,
} from "Assessments/modules/Utils";

// Constants
import { MYP_INTERDISCIPLINARY, UNIT } from "Constants/stringConstants";
import {
  INTERDISCIPLINARY_FIELD_MAPPING,
  LOCAL_CONTEXT,
  MYP_OBJECTIVES,
  ELEMENT_TYPE_ADD_LEARNING_GOALS,
} from "../../modules/Constants";

const FIELD_DATA_CONFIG = {
  centralIdea: ({ t }) => ({
    label: t("common:centralIdea_label"),
    isSelectable: false,
  }),
  goals: ({ t }) => ({
    label: t("common:capitalize", { text: t("common:learning_intentions") }),
    isSelectable: false,
  }),
  title: ({ t }) => ({
    label: t("common:title"),
    isSelectable: false,
  }),
  location: ({ t }) => ({
    label: t("common:location"),
    isSelectable: false,
  }),
  unitPlan: ({ t }) => ({
    label: t("classRoom:associated_unit"),
    isSelectable: false,
  }),
  subjects: ({ t }) => ({
    label: t("common:subject_plural"),
    isSelectable: false,
  }),
  concepts: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["concepts"]),
    isSelectable: true,
  }),
  soi: ({ t }) => ({
    label: t("unitPlanTemplate:unitPlan_MYP_SOI_label"),
  }),
  inquiryQuestions: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["inquiryQuestions"]),
  }),
  conceptsMYP: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["conceptsMYP"]),
    isSelectable: true,
  }),
  globalContextMYP: ({ t }) => ({
    label: t("progressReport:global_context"),
    isSelectable: true,
  }),
  content: ({ t }) => ({
    label: t("common:content_label"),
  }),
  disciplinaryContent: ({ t }) => ({
    label: t("common:content_label"),
  }),
  relatedConceptsMYP: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["relatedConceptsMYP"]),
    isSelectable: true,
  }),
  objectivesMYP: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["objectivesMYP"]),
    isSelectable: true,
  }),
  interdisciplinaryCriteriaMYP: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["interdisciplinaryCriteriaMYP"]),
    isSelectable: true,
  }),
  atlsMYP: ({ t }) => ({
    label: t(plannerElementTypesLabelMapping["atls"]),
    isSelectable: true,
  }),
  standardBasedRubric: ({ t }) => ({
    label: t("common:standard_based_rubric"),
  }),
};

const FIELD_COMPONENT_PROPS_CONFIG = {
  title: ({ t }) => ({
    label: t("common:title"),
    type: "InputField",
  }),
  disciplinaryContent: ({ allFields }) => {
    if (getIsMYPInterdiscplinaryUnit(allFields)) {
      return {
        label: "",
        subtext: "",
      };
    }

    return {};
  },
};

export const getCurrentFieldConfig = params => {
  const {
    t,
    step,
    field,
    stepList,
    fieldList,
    fieldConfig,
    isUnitDependent,
    unitPlanFieldList,
    unitPlanStepList,
  } = params;

  const currentField = FIELD_DATA_CONFIG?.[field]?.({ t });

  const config = {
    ...currentField,
    ...getIsFieldDisabled(params),
    hasValue: getFieldHasValue(params),
    showSkipButton: getShowSkipButton(params),
  };

  const buttonLabel = getButtonLabel({
    ...params,
    ...config,
  });

  if (!config?.label) {
    if (fieldConfig?.label) {
      _.set(config, "label", fieldConfig.label);
    } else {
      const label = t(_.get(plannerElementTypesLabelMapping, field));

      if (label) {
        _.set(config, "label", label);
      } else {
        if (step) {
          const _stepList = isUnitDependent ? unitPlanStepList : stepList;
          _.set(config, "label", _.get(_stepList, `${step}.label`));
        } else {
          const _fieldList = isUnitDependent ? unitPlanFieldList : fieldList;
          _.set(config, "label", _.get(_fieldList, `${field}.config.label`));
        }
      }
    }
  }

  return {
    ...config,
    buttonLabel,
  };
};

export const getShowSkipButton = params => {
  const { field, fieldConfig } = params;

  // show skip button is field is skippable
  if (fieldConfig && _.get(fieldConfig, "isSkippable", false)) return true;

  // if objectivesMYP and interdisciplinaryCriteriaMYP both are asked as prerequisite then
  // when one of them is filled, show skip button for the other
  if (
    !MYP_OBJECTIVES.includes(field) ||
    _.every(MYP_OBJECTIVES, field => getFieldHasValue({ ...params, field }))
  ) {
    return false;
  }

  let fieldToCheck = null;

  switch (field) {
    case "objectivesMYP":
      fieldToCheck = "interdisciplinaryCriteriaMYP";
      break;
    case "interdisciplinaryCriteriaMYP":
      fieldToCheck = "objectivesMYP";
      break;
  }

  return getFieldHasValue({
    ...params,
    field: fieldToCheck,
  });
};

export const getFieldHasValue = params => {
  const {
    field,
    step,
    context,
    stepList,
    fieldList,
    assessmentDetails,
    unitPlanDetails,
    isUnitDependent,
    unitPlanFieldList,
    unitPlanStepList,
  } = params;

  const { unitPlanFields } = unitPlanDetails || {};

  const isDynamicField =
    getIsMYPInterdiscplinaryUnit(unitPlanFields) &&
    isUnitDependent &&
    INTERDISCIPLINARY_FIELD_MAPPING[field];

  // some fields like disciplinaryObjectivesMYP are subjects dependented
  // and are stored like disciplinaryObjectivesMYP_12345 with subject id
  // this if block handles that case
  if (isDynamicField) {
    const fields = _.map(
      _.filter(unitPlanFields, ({ uid }) =>
        _.startsWith(uid, INTERDISCIPLINARY_FIELD_MAPPING[field])
      ),
      "uid"
    );

    return _.some(fields, field => {
      return getSelectedFieldHasValue({
        assessmentDetails: {
          allFields: unitPlanFields,
        },
        selectedAssessmentField: field,
        context,
        step,
        stepList: unitPlanStepList,
        fieldList: unitPlanFieldList,
      });
    });
  }

  if (isUnitDependent && _.has(unitPlanFieldList?.[field], "subFields")) {
    const subFields = _.get(unitPlanFieldList?.[field], "subFields");

    return _.some(subFields, subField => {
      const uid = `${field}_${subField.key}`;

      return getSelectedFieldHasValue({
        assessmentDetails: {
          allFields: unitPlanFields,
        },
        selectedAssessmentField: uid,
        context,
        step,
        stepList: unitPlanStepList,
        fieldList: unitPlanFieldList,
      });
    });
  }

  // if field is not dynamic then check if it has value
  // depending on whether it is unitPlan field or assessment
  if (isUnitDependent) {
    return getSelectedFieldHasValue({
      assessmentDetails: {
        allFields: unitPlanFields,
      },
      selectedAssessmentField: field,
      step,
      stepList: unitPlanStepList,
      context,
      fieldList: unitPlanFieldList,
    });
  } else {
    let selectedAssessmentField = field;

    const assessmentTool = getAssessmentToolFromField({ field });

    if (!_.isEmpty(assessmentTool)) {
      selectedAssessmentField = _.get(assessmentTool, "type");
    }

    return getSelectedFieldHasValue({
      assessmentDetails,
      selectedAssessmentField,
      context,
      step,
      stepList,
      fieldList,
    });
  }
};

export const getFieldSelectionModalWidth = type => {
  if (!type) return "fit-content";

  switch (type) {
    case "RichText":
    case "IBTheme":
    case "MultiSelectWithIcon":
    case "MultiField":
    case "InputNumberList":
      return "648px";
    case "IBMultiSelect":
      return "720px";
    case "MultiSelect":
      return "fit-content";
    default:
      return "472px";
  }
};

export const getFieldSelectionModalHeight = type => {
  switch (type) {
    case "FieldDropdown":
    case "LearningIntentions":
      return "480px";
    default:
      return "fit-content";
  }
};

export const getButtonLabel = params => {
  const {
    t,
    isUnitDependent,
    isSelectable,
    entityLabel,
    hasValue,
    fieldConfig,
  } = params;

  const { buttonLabel, emptyValueButtonLabel } = fieldConfig || {};

  if (buttonLabel && hasValue) return buttonLabel;
  else if (emptyValueButtonLabel && !hasValue) return emptyValueButtonLabel;

  let key = "";

  if (isUnitDependent) {
    if (isSelectable) {
      if (hasValue) {
        key = "selected_for_with_label";
      } else {
        key = "select_for_with_label";
      }
    } else {
      if (hasValue) {
        key = "added_to_with_label";
      } else {
        key = "add_to_label";
      }
    }
  } else {
    if (isSelectable) {
      if (hasValue) {
        key = "selected_for_with_label";
      } else {
        key = "select_for_with_label";
      }
    } else {
      if (hasValue) {
        key = "added_for_with_label";
      } else {
        key = "add_for_with_label";
      }
    }
  }

  return t(`common:${key}`, { label: entityLabel });
};

export const getIsFieldDisabled = params => {
  const {
    t,
    field,
    type,
    fieldList,
    isUnitDependent,
    assessmentDetails,
    unitPlanDetails,
    unitPlanFieldList,
    context,
    step,
    unitPlanStepList,
  } = params;

  const { unitPlanFields } = unitPlanDetails || {};

  const isUnitSelected = getSelectedFieldHasValue({
    assessmentDetails,
    selectedAssessmentField: "unitPlan",
  });

  // if unit delinking is enabled then disable unit dependent fields until unit is selected
  if (isUnitDependent) {
    if (field === "title") return { disabled: false };
    if (!isUnitSelected) {
      return {
        showField: false,
      };
    }
  }

  const isInBothUnitAndAssessment = getIsFieldInBothUnitAndAssessment({
    field,
    fieldList,
    unitPlanFieldList,
  });

  // for fields which are in both unit and assessments like lois
  if (isInBothUnitAndAssessment && _.startsWith(type, "ASSESSMENT_")) {
    const isMYPInterdiscplinaryUnit = getIsMYPInterdiscplinaryUnit(
      unitPlanFields
    );
    const interdisciplinaryFieldUID = INTERDISCIPLINARY_FIELD_MAPPING[field];

    const isDynamicField =
      isMYPInterdiscplinaryUnit && interdisciplinaryFieldUID;

    let disabledTooltip = "";
    if (plannerElementTypesLabelMapping?.[field]) {
      disabledTooltip = `Please select ${t(
        plannerElementTypesLabelMapping[field]
      )} in unit first`;
    }

    if (_.has(fieldList?.[field], "subFields")) {
      const subFields = _.get(fieldList?.[field], "subFields");

      const hasValue = _.some(subFields, subField => {
        const uid = `${field}_${subField.key}`;
        return getSelectedFieldHasValue({
          assessmentDetails: {
            allFields: unitPlanFields,
          },
          selectedAssessmentField: uid,
          fieldList: unitPlanFieldList,
          step,
          stepList: unitPlanStepList,
          context,
        });
      });

      return { disabled: !hasValue, disabledTooltip };
    }

    if (isDynamicField) {
      const fields = _.map(
        _.filter(unitPlanFields, ({ uid }) =>
          _.startsWith(uid, interdisciplinaryFieldUID)
        ),
        "uid"
      );

      const hasValue = _.some(fields, field => {
        return getSelectedFieldHasValue({
          assessmentDetails: {
            allFields: unitPlanFields,
          },
          selectedAssessmentField: field,
          fieldList: unitPlanFieldList,
          step,
          stepList: unitPlanStepList,
          context,
        });
      });

      if (!hasValue) {
        return { disabled: true, disabledTooltip };
      } else {
        return { disabled: false };
      }
    }

    const hasValue = getSelectedFieldHasValue({
      assessmentDetails: {
        allFields: unitPlanFields,
      },
      selectedAssessmentField: field,
      fieldList: unitPlanFieldList,
      stepList: unitPlanStepList,
      step,
      context,
    });

    if (!hasValue) return { disabled: true, disabledTooltip };
  }

  return { disabled: false };
};

export const getGroupedFields = params => {
  const {
    t,
    fields = [],
    fieldList,
    unitPlanFieldList,
    unitPlanDetails,
    assessmentDetails,
    stepList,
    unitPlanStepList,
  } = params;

  const { unitPlanFields } = unitPlanDetails || {};

  let updatedFields = fields;

  const isNewFlow = _.every(
    updatedFields,
    ({ type } = {}) => !_.includes(type, "_")
  );

  if (isNewFlow) {
    updatedFields = _.map(updatedFields, ({ field, type, ...rest } = {}) => {
      const _stepList = type === UNIT ? unitPlanStepList : stepList;

      return {
        ...rest,
        field,
        type: `${type}_${field}`,
        step: _.get(_stepList, field) ? field : null,
      };
    });
  }

  // unit should be the first field in the list
  const sortedFields = _.sortBy(updatedFields, item => {
    const [type, field] = _.split(item?.type, "_") || [];
    return field !== "unitTitle" || (type === UNIT && field === "title");
  });

  updatedFields = _.reduce(
    sortedFields,
    (acc, item) => {
      const { type, step } = item || {};
      const field = _.last(_.split(type, "_"));

      const isInBothUnitAndAssessment = getIsFieldInBothUnitAndAssessment({
        field,
        fieldList,
        unitPlanFieldList,
      });

      const isUnitFieldExist = !_.isEmpty(
        _.find(sortedFields, ({ type } = {}) => {
          const [entity, fieldUID] = _.split(type, "_") || [];
          return entity === "UNIT" && field === fieldUID;
        })
      );

      if (isInBothUnitAndAssessment && !isUnitFieldExist) {
        const unitFieldHasValue = getSelectedFieldHasValue({
          assessmentDetails: {
            allFields: unitPlanFields,
          },
          selectedAssessmentField: field,
          fieldList: unitPlanFieldList,
          stepList: unitPlanStepList,
          step,
        });

        const assessementFieldHasValue = getSelectedFieldHasValue({
          assessmentDetails,
          selectedAssessmentField: field,
          fieldList,
          stepList,
          step,
        });

        if (!unitFieldHasValue && !assessementFieldHasValue) {
          acc.push({
            field,
            type: `UNIT_${field}`,
          });
        }
      }

      acc.push(item);

      return acc;
    },
    []
  );

  // if any unit field prerequisite is there without unit
  // then add unit pre-requisite field
  const hasUnitPrerequisite = !_.isEmpty(
    _.find(updatedFields, field => {
      const { type } = field || {};
      const [entity, fieldUID] = _.split(type, "_") || [];
      return entity === "UNIT" && fieldUID === "title";
    })
  );

  const requireUnitPrerequisite =
    !hasUnitPrerequisite &&
    _.some(updatedFields, field => {
      const { type } = field || {};
      return _.first(_.split(type, "_")) === "UNIT";
    }) &&
    _.isEmpty(
      getFieldValue({
        fields: assessmentDetails?.allFields,
        uid: "unitPlan",
      })
    );

  if (requireUnitPrerequisite) {
    updatedFields = [
      {
        field: isNewFlow ? "title" : "unitTitle",
        type: "UNIT_title",
        step: null,
        config: null,
      },
      ...updatedFields,
    ];
  }

  const localisedFields = _.map(updatedFields, field => {
    const { localisedKeys } = field || {};

    if (!_.isEmpty(localisedKeys)) {
      _.map(localisedKeys, key => {
        if (_.isString(field[key])) {
          field[key] = t(field[key]);
        } else if (_.isArray(field[key])) {
          field[key] = _.map(field[key], item => {
            return {
              ...item,
              label: t(item?.label),
            };
          });
        }
      });
    }

    return field;
  });

  const groupedFields = _.groupBy(localisedFields, field =>
    _.first(field?.type?.split("_"))
  );

  const result = {
    UNIT: groupedFields["UNIT"] || [],
    ..._.omit(groupedFields, "UNIT"),
  };

  return _.pickBy(result, value => !_.isEmpty(value));
};

export const getFieldComponentProps = params => {
  const { type, field, allFields, defaultOptions, fieldTypeConfig } = params;

  let { value } = params;

  const shouldInitOptions = _.get(fieldTypeConfig, "shouldInitOptions", false);

  // to make things backward compatible for pedagogicalApproachDP
  if (field === "pedagogicalApproachDP") {
    if (_.every(value, item => _.has(item, "value"))) {
      value = _.map(
        _.filter(value, item => _.get(item, "isSelected", false)),
        "id"
      );
    }
  } else if (shouldInitOptions) {
    // if value and options are not same for it means that options has been changed
    // setting value to empty value will re-init the value
    if (_.size(_.intersectionBy(value, defaultOptions, "id")) <= 0) {
      value = [];
    }
  }

  return {
    mode: "edit",
    fieldKey: field,
    name: field,
    value,
    type,
    assessmentFields: allFields,
    resourceFields: allFields,
    uiType: type === "FieldDropdown" ? "BUTTON_DISCLOSURE" : "BUTTON",
    applyDefaultStyles: false,
    isRequired: false,
    showReflection: false,
    ...(FIELD_COMPONENT_PROPS_CONFIG?.[field]?.(params) || {}),
  };
};

export const getTypeToRender = params => {
  const { fieldList, unitPlanFieldList, field, isUnitDependent } = params;

  // fields with InputNumberList type should render with it's own type (lois etc.)
  const isInputNumberList =
    _.get(unitPlanFieldList, `${field}.type`) === "InputNumberList";

  if (isUnitDependent && isInputNumberList) {
    return _.get(unitPlanFieldList, `${field}.type`);
  }

  // render learning goals (both unit plans (except few) and assessments) through AddLearningGoals
  const learningGoalsOptions = _.get(
    fieldList,
    `addLearningGoals.config.options`,
    []
  );

  const learningGoalsElementKeys = _.map(learningGoalsOptions, "key");

  if (_.includes(learningGoalsElementKeys, field))
    return ELEMENT_TYPE_ADD_LEARNING_GOALS;

  // render rest of the fields from the type specified in the planner template
  const assessmentFieldType = _.get(fieldList, `${field}.type`);
  const unitPlanFieldType = _.get(unitPlanFieldList, `${field}.type`);

  return isUnitDependent ? unitPlanFieldType : assessmentFieldType;
};

export const getModalTitle = ({
  t,
  field,
  fieldList,
  unitPlanFields,
  isUnitDependent,
  assessmentTypeLabel,
  stepList,
  fieldConfig,
}) => {
  const unitTitle = getFieldValue({
    fields: unitPlanFields,
    uid: "title",
  });

  switch (field) {
    case "title":
      return t("common:add_label1_for_label2_without_quotes", {
        label1: t("common:title"),
        label2: t("common:lowercase", {
          text: isUnitDependent ? t("common:unit") : t(assessmentTypeLabel),
        }),
      });
    case "unitPlan":
    case "unitTitle":
      return t("common:select_with_label", {
        label: t("common:unit"),
      });
    case "centralIdea":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:centralIdea_label"),
        label2: unitTitle,
      });
    case "soi":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("unitPlanTemplate:unitPlan_MYP_SOI_label"),
        label2: unitTitle,
      });
    case "globalContextMYP":
      return t("common:select_label1_for_label2", {
        label1: t("unitPlanTemplate:unitPlan_globalContextMYP_label"),
        label2: unitTitle,
      });
    case "content":
    case "disciplinaryContent":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:content_label"),
        label2: unitTitle,
      });
    case "inquiryQuestions": {
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:inquiry_questions"),
        label2: unitTitle,
      });
    }
    default: {
      const fieldLabel = _.get(fieldList, `${field}.config.label`);
      const stepLabel = _.get(stepList, `${field}.label`);

      if (fieldLabel) return t("common:add_with_label", { label: fieldLabel });
      else if (stepLabel)
        return t("common:add_with_label", { label: stepLabel });
      else {
        if (!_.isEmpty(fieldConfig)) {
          const { modalLabel, options, label } = fieldConfig || {};

          if (!_.isEmpty(modalLabel)) return modalLabel;
          else if (!_.isEmpty(options)) {
            return t("common:select_with_label", { label });
          } else {
            return t("common:add_with_label", {
              label,
            });
          }
        } else {
          return t("common:add");
        }
      }
    }
  }
};

export const getIsMYPInterdiscplinaryUnit = unitPlanFields => {
  const unitType = _.get(_.find(unitPlanFields, { uid: "unitType" }), "value");
  return unitType === MYP_INTERDISCIPLINARY;
};

export const getIsDynamicField = params => {
  const { field, isUnitDependent, unitPlanFields } = params;

  const interdisciplinaryFieldUID = INTERDISCIPLINARY_FIELD_MAPPING[field];

  return (
    isUnitDependent &&
    getIsMYPInterdiscplinaryUnit(unitPlanFields) &&
    interdisciplinaryFieldUID
  );
};

export const updateLearningGoals = args => {
  const {
    t,
    item,
    field,
    portalType,
    isUnitDependent,
    unitPlanFields,
    isDynamicField,
    updateUnitPlanField,
    unitPlanFieldList,
    unitPlanId,
    refetchAssessments,
    updateAssignmentField,
    assessmentDetails,
    fieldList,
    setUpdateParams,
    updateInputField,
  } = args;

  const { extraParams, params } = item || {};

  if (isDynamicField) {
    const { nodes } = extraParams || {};
    const values = _.get(params, field, []);

    // reset values of all the dyanmic fields
    if (_.isEmpty(values)) {
      const fields = _.filter(unitPlanFields, ({ uid }) => {
        return _.startsWith(uid, INTERDISCIPLINARY_FIELD_MAPPING[field]);
      });

      _.map(fields, field => {
        updateUnitPlanField({
          t,
          ...item,
          params: {
            [`${field.uid}`]: [],
          },
          isDebounce: false,
          portalType,
          unitPlanFields,
          field_list: unitPlanFieldList,
          unitPlanId,
        });
      });
    } else {
      const resolvedSubjectNodes = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      const subjectValueMap = _.reduce(
        resolvedSubjectNodes,
        (acc, { id } = {}) => {
          if (id && !acc[id]) {
            acc[id] = [];
            const subjectValue = _.filter(values, value => {
              const currentNode = _.find(nodes, node => {
                return node?.id === value;
              });

              const currentNodeSubjectId = _.get(
                _.find(currentNode?.associatedParents, parent => {
                  return _.get(parent, "__typename") === "Subject";
                }),
                "id"
              );

              return currentNodeSubjectId === id;
            });

            acc[id].push(...subjectValue);
          }

          return acc;
        },
        {}
      );

      _.map(subjectValueMap, (values, subjectId) => {
        updateUnitPlanField({
          t,
          ...item,
          params: {
            [`${INTERDISCIPLINARY_FIELD_MAPPING[field]}_${subjectId}`]: values,
          },
          isDebounce: false,
          portalType,
          unitPlanFields,
          field_list: unitPlanFieldList,
          unitPlanId,
        });
      });
    }

    setTimeout(() => {
      refetchAssessments?.();
    }, 500);
  } else {
    setUpdateParams(params);
    updateInputField(item);
  }

  // when user changes the learning goals in unit plan then check if those learning goals were tagged in the assessment
  // if they were tagged then update the assessment learning goals as well (remove the one's which are not in unit plan)
  const isInBothUnitAndAssessment =
    getIsFieldInBothUnitAndAssessment({
      field,
      fieldList,
      unitPlanFieldList,
    }) && isUnitDependent;

  if (isInBothUnitAndAssessment) {
    const assessmentFieldValue = getFieldValue({
      fields: assessmentDetails?.allFields,
      uid: field,
    });

    const unitPlanFieldValue = getFieldValue({
      fields: unitPlanFields,
      uid: field,
    });

    const valuesToRemove = _.difference(unitPlanFieldValue, params[field]);

    if (!_.isEmpty(_.intersection(assessmentFieldValue, valuesToRemove))) {
      const commonParams = {
        t,
        ...item,
        params: {
          [`${field}`]: _.filter(
            assessmentFieldValue,
            val => !_.includes(valuesToRemove, val)
          ),
        },
        isDebounce: false,
        portalType,
      };

      updateAssignmentField({
        ...commonParams,
        assessmentDetails,
        field_list: fieldList,
      });
    }
  }
};

export const getSubFields = params => {
  const { t, unitPlanFields, fieldList, field, unitPlanTemplate } = params;

  const isMYPInterdiscplinaryUnit = getIsMYPInterdiscplinaryUnit(
    unitPlanFields
  );

  const resolvedSubjectNodes = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );

  if (isMYPInterdiscplinaryUnit && field === "disciplinaryContent") {
    return _.reduce(
      resolvedSubjectNodes,
      (acc, node) => {
        acc.push({
          config: {
            label: t("common:add_label1_for_label2_without_quotes", {
              label1: t("common:content_label"),
              label2: node?.name,
            }),
            subText: _.get(
              unitPlanTemplate,
              "field_list.disciplinaryContent.config.subtext"
            ),
          },
          key: node?.id,
          printType: "BaseComponent",
          type: "RichText",
          viewType: "TextView",
        });

        return acc;
      },
      []
    );
  }

  return _.get(fieldList?.[field], "subFields", []);
};

export const getEntityAndFieldType = params => {
  const { key, type, unitPlanDetails, assessmentDetails } = params;

  let [entity, field] = _.split(type, "_") || [];

  if (key === "unitTitle" || (entity === UNIT && field === "title")) {
    const unitPlan = getFieldValue({
      fields: assessmentDetails?.allFields,
      uid: "unitPlan",
    });

    const unitPlanTitle = getFieldValue({
      fields: _.get(unitPlanDetails, "unitPlanFields", []),
      uid: "title",
    });

    if (_.isEmpty(unitPlan)) {
      field = "unitPlan";
      entity = "ASSESSMENT";
    } else if (_.isEmpty(unitPlanTitle)) {
      field = "title";
      entity = "UNIT";
    } else {
      field = "unitPlan";
      entity = "ASSESSMENT";
    }
  }

  return [entity, field];
};

export const getDependentFields = params => {
  const { fieldList, field } = params;

  const keys = [];
  _.forEach(fieldList, (item, key) => {
    const config = _.get(item, "config", {});
    const isFilteredBy = _.includes(
      config?.plannerElementConfig?.filteredBy,
      field
    );

    if (isFilteredBy) keys.push(key);
  });

  return keys;
};

export const isLocalContext = ({ field, config }) => {
  return _.includes(LOCAL_CONTEXT, field) || config?.isLocalField;
};

export const getIsFieldInBothUnitAndAssessment = params => {
  const { field, fieldList, unitPlanFieldList } = params;

  return (
    !_.isEmpty(fieldList?.[field]) &&
    !_.isEmpty(unitPlanFieldList?.[field]) &&
    _.get(unitPlanFieldList, `${field}.type`) === "InputNumberList"
  );
};

export const getAssessmentToolFromField = params => {
  const { field } = params;

  return _.find(ASSESSMENT_TOOLS, ({ key, prerequisiteType }) => {
    return prerequisiteType ? prerequisiteType === field : key === field;
  });
};
